import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronDown,
  LogOut,
  User,
  Building,
  ShieldCheck,
  Megaphone,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import { ArterraLogo } from '@/components/ui/ArterraLogo';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useSidebar } from '@/contexts/SidebarContext';
import { useAuth } from '@/hooks/useAuth';
import { useUserProfile } from '@/hooks/queries/useUser';
import { useHasVerifiedClaim } from '@/hooks/queries/useOwner';
import { getSidebarConfig, type SidebarItem, type SidebarSection } from './sidebarConfig';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

function useActivePath() {
  const location = useLocation();
  const isActive = useCallback(
    (to: string, matchPaths?: string[]) => {
      const current = location.pathname;
      if (current === to) return true;
      // Tab deep links (e.g. `/admin?tab=raises`) match on pathname + search.
      if (to.includes('?')) {
        const currentWithSearch = `${location.pathname}${location.search}`;
        if (currentWithSearch === to || currentWithSearch.startsWith(`${to}&`)) return true;
      }
      if (matchPaths?.some((p) => current.startsWith(p))) return true;
      if (to !== '/' && to !== '/home' && !to.includes('?') && current.startsWith(to)) return true;
      return false;
    },
    [location.pathname, location.search],
  );
  return isActive;
}

function SidebarNavItem({
  item,
  isCollapsed,
  depth = 0,
}: {
  item: SidebarItem;
  isCollapsed: boolean;
  depth?: number;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isActive = useActivePath();
  const location = useLocation();
  const active = isActive(item.to, item.matchPaths);
  const hasChildren = item.children && item.children.length > 0;

  useEffect(() => {
    if (hasChildren && item.children?.some((child) => isActive(child.to, child.matchPaths))) {
      setIsExpanded(true);
    }
  }, [location.pathname, hasChildren, item.children, isActive]);

  const Icon = item.icon;

  if (hasChildren && !isCollapsed) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className={cn(
            'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative',
            active
              ? 'text-white bg-white/10'
              : 'text-white/70 hover:text-white hover:bg-white/5',
          )}
        >
          {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-amber-400 rounded-r" />}
          <Icon className={cn('h-[18px] w-[18px] shrink-0', active ? 'text-white' : 'text-white/50')} />
          <span className="flex-1 text-left">{item.label}</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 text-white/40 transition-transform duration-200',
              isExpanded && 'rotate-180',
            )}
          />
        </button>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="overflow-hidden"
            >
              <div className="ml-3 mt-0.5 space-y-0.5">
                {item.children!.map((child) => (
                  <SidebarNavItem key={child.id} item={child} isCollapsed={false} depth={depth + 1} />
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  const linkContent = (
    <Link
      to={item.to}
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors relative',
        isCollapsed && 'justify-center px-0',
        active
          ? 'text-white bg-white/10'
          : 'text-white/70 hover:text-white hover:bg-white/5',
        depth > 0 && 'text-[13px]',
      )}
      aria-current={active ? 'page' : undefined}
    >
      {active && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-amber-400 rounded-r" />}
      <Icon className={cn('h-[18px] w-[18px] shrink-0', active ? 'text-white' : 'text-white/50')} />
      {!isCollapsed && <span>{item.label}</span>}
    </Link>
  );

  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right" sideOffset={8}>
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return linkContent;
}

function SidebarSectionBlock({
  section,
  isCollapsed,
}: {
  section: SidebarSection;
  isCollapsed: boolean;
}) {
  const [isOpen, setIsOpen] = useState(!section.collapsible);

  if (isCollapsed) {
    return (
      <div className="space-y-1 py-2">
        {section.items.map((item) => (
          <SidebarNavItem key={item.id} item={item} isCollapsed />
        ))}
      </div>
    );
  }

  return (
    <div className="py-2">
      {section.collapsible ? (
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center justify-between w-full px-3 mb-1"
        >
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
            {section.label}
          </span>
          <ChevronDown
            className={cn(
              'h-3 w-3 text-white/40 transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        </button>
      ) : (
        <div className="px-3 mb-1">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
            {section.label}
          </span>
        </div>
      )}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={section.collapsible ? { height: 0, opacity: 0 } : false}
            animate={{ height: 'auto', opacity: 1 }}
            exit={section.collapsible ? { height: 0, opacity: 0 } : undefined}
            transition={{ duration: 0.15 }}
            className="space-y-0.5 overflow-hidden"
          >
            {section.items.map((item) => (
              <SidebarNavItem key={item.id} item={item} isCollapsed={false} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function UserSection({ isCollapsed }: { isCollapsed: boolean }) {
  const { user, isAdmin, clearCacheAndLogout } = useAuth();
  const { data: userProfile } = useUserProfile();
  const navigate = useNavigate();
  // Dual-role owner entry (G-SIGNUP-2): a developer/investor/admin who has claimed a
  // raise keeps their primary role AND owns the raise via a VERIFIED claim, but lands
  // in this shell with no visible path to their console. Surface a link to `/owner`
  // ONLY when the account actually holds a verified claim (sign-in-gated signal,
  // server-scoped to the caller's own claims — empty/false for a non-owner, so the
  // link is never shown speculatively). The scoped `raise_owner` persona never
  // renders this shell (it gets OwnerLayout), so this affordance is exclusively for
  // dual-role accounts.
  const { hasVerifiedClaim } = useHasVerifiedClaim();

  const handleLogout = useCallback(async () => {
    try {
      await clearCacheAndLogout();
      toast.success('Logged out successfully');
    } catch {
      toast.error('Error logging out');
    }
  }, [clearCacheAndLogout]);

  const email = user?.email ?? '';
  const initial = (userProfile?.firstName?.[0] ?? email[0] ?? 'U').toUpperCase();

  if (isCollapsed) {
    return (
      <div className="px-2 py-3 border-t border-white/10">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => navigate('/profile')}
              className="w-full flex items-center justify-center"
            >
              <div className="w-8 h-8 rounded-full bg-white/15 text-white flex items-center justify-center text-xs font-semibold">
                {initial}
              </div>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            {email}
          </TooltipContent>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="px-3 py-3 border-t border-white/10 space-y-1">
      <div className="flex items-center gap-2.5 px-2 py-1.5">
        <div className="w-7 h-7 rounded-full bg-[#E9A634] text-slate-900 flex items-center justify-center text-xs font-bold shrink-0">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-white truncate">
            {userProfile?.firstName
              ? `${userProfile.firstName} ${userProfile?.lastName ?? ''}`.trim()
              : email}
          </p>
          {userProfile?.firstName && (
            <p className="text-[10px] text-white/50 truncate">{email}</p>
          )}
        </div>
        {isAdmin && (
          <span className="text-[9px] font-semibold uppercase tracking-wide text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded shrink-0">
            Admin
          </span>
        )}
      </div>
      <Link
        to="/profile"
        className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
      >
        <User className="h-4 w-4 text-white/50" />
        <span>Profile</span>
      </Link>
      <Link
        to="/organization"
        className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
      >
        <Building className="h-4 w-4 text-white/50" />
        <span>Organization</span>
      </Link>
      {hasVerifiedClaim && (
        <Link
          to="/owner"
          className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
        >
          <Megaphone className="h-4 w-4 text-amber-400" />
          <span>Owner Console</span>
        </Link>
      )}
      {isAdmin && (
        <Link
          to="/admin"
          className="flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors"
        >
          <ShieldCheck className="h-4 w-4 text-white/50" />
          <span>Admin Dashboard</span>
        </Link>
      )}
      <button
        type="button"
        onClick={handleLogout}
        className="w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-sm text-white/70 hover:text-red-300 hover:bg-red-500/10 transition-colors"
      >
        <LogOut className="h-4 w-4 text-white/50" />
        <span>Logout</span>
      </button>
    </div>
  );
}

export function AppSidebar() {
  const { isCollapsed, isMobile, isMobileOpen, toggleSidebar, closeMobileSidebar } = useSidebar();
  const { isAdmin } = useAuth();
  const location = useLocation();

  const sections = getSidebarConfig({ isAdmin });

  useEffect(() => {
    closeMobileSidebar();
  }, [location.pathname, closeMobileSidebar]);

  useEffect(() => {
    if (!isMobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMobileSidebar();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [isMobileOpen, closeMobileSidebar]);

  const sidebarWidth = isCollapsed ? 'w-[60px]' : 'w-[260px]';

  const sidebarContent = (
    <div
      className={cn(
        'flex flex-col h-full bg-slate-900 transition-all duration-200',
        isMobile ? 'w-[260px]' : sidebarWidth,
      )}
      role="navigation"
      aria-label="Main Navigation"
    >
      {/* Logo + collapse toggle */}
      <div className={cn(
        'flex items-center h-14 shrink-0 border-b border-white/10',
        isCollapsed && !isMobile ? 'px-0' : 'px-4',
      )}>
        {isCollapsed && !isMobile ? (
          <div className="flex items-center justify-center w-full">
            <Link to="/home" className="flex items-center justify-center">
              <ArterraLogo size={28} />
            </Link>
          </div>
        ) : (
          <>
            <Link to="/home" className="flex items-center gap-2.5">
              <ArterraLogo size={32} />
              <span className="text-lg font-semibold text-white tracking-tight">Arterra AI</span>
            </Link>
            {!isMobile && (
              <button
                type="button"
                onClick={toggleSidebar}
                className="ml-auto p-1 rounded-md text-white/40 hover:text-white/70 hover:bg-white/10 transition-colors"
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose className="h-4 w-4" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Expand toggle — shown inside collapsed sidebar above nav items */}
      {isCollapsed && !isMobile && (
        <div className="px-2 pt-2 pb-0.5 shrink-0">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggleSidebar}
                className="w-full flex items-center justify-center py-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Expand sidebar"
              >
                <PanelLeft className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              Expand sidebar
            </TooltipContent>
          </Tooltip>
        </div>
      )}

      {/* Navigation sections */}
      <div className="flex-1 overflow-y-auto px-2 py-1 scrollbar-hide">
        {sections.map((section) => (
          <SidebarSectionBlock
            key={section.id}
            section={section}
            isCollapsed={isCollapsed && !isMobile}
          />
        ))}
      </div>

      {/* User section */}
      <UserSection isCollapsed={isCollapsed && !isMobile} />
    </div>
  );

  // Mobile: overlay drawer
  if (isMobile) {
    return (
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
              onClick={closeMobileSidebar}
              aria-hidden="true"
            />
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="fixed inset-y-0 left-0 z-50"
            >
              {sidebarContent}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  }

  return sidebarContent;
}
